import {
  Menu,
  MenuButton,
  MenuItems,
  MenuLink,
  MenuPopover,
} from "@reach/menu-button"
import { Link as GatsbyLink, navigate } from "gatsby"
import debounce from "lodash/debounce"
import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react"
import ThemeContext from "../context/ThemeContext"
import logo from "../img/intralox-logo-box.svg"
import { urlHelper } from "../util"
import { getLocalizedString } from "../utils/getLocalizedString"
import { getUrlPrefix } from "../utils/getUrlPrefix"
import Stickyfill from "../utils/stickyfill"
import Link from "./LinkWrapper"
import LocalNav from "./LocalNav"
import { MainNavigation } from "./MainNavigation"
import { PageContext } from "./PageProvider"
import { useLocation } from "@reach/router"

const languages = [
  { title: `Deutsch`, codename: `de` },
  { title: `English (U.S.)`, codename: `en` },
  { title: `Español (España)`, codename: `es` },
  { title: `Français`, codename: `fr` },
  { title: `Italiano`, codename: `it` },
  { title: `日本語`, codename: `jp` },
  { title: `Polski`, codename: `pl` },
  { title: `Português (Brasil)`, codename: `pt` },
  { title: `中文`, codename: `cn` },
]

export interface HeaderProps {
  /**
   * Current language of the website
   * Codename for current language. Used to prefix search and homepage URLs
   */
  currentLanguage: "en" | "de" | "es" | "fr" | "it" | "pl" | "pt" | "jp" | "cn"
  /**
   * The content item for the current page
   */
  entry: object
  /**
   * An array of data for other language variants used to display links to other languages in the language switcher
   */
  otherLanguages: object[]
  /**
   * Global content object that is passed into `<MainNavigation/>`
   */
  global: object
  /**
   * An array of language-specific localized strings
   */
  localizedStrings: any
  /**
   * An array of objects that contains the entries for the sub nav
   */
  localNav: any
}

/**
 * The header component for the website
 */
const Header = ({
  currentLanguage,
  entry,
  otherLanguages,
  global,
  localizedStrings,
  localNav,
}: HeaderProps) => {
  const { hydrated: isClient, setHeaderHeight } = useContext(ThemeContext)
  const pageCtx = useContext(PageContext)
  const urlPrefix = getUrlPrefix(currentLanguage)
  const localizedCallUs = getLocalizedString(
    localizedStrings.nodes,
    "localization_string___call_us",
    "Call Us"
  )
  const localizedGetAQuote = getLocalizedString(
    localizedStrings.nodes,
    "localization_string___get_a_quote",
    "Get a Quote"
  )
  const localizedLanguage = getLocalizedString(
    localizedStrings.nodes,
    "localization_string___language",
    "Language"
  )
  const localizedSearch = getLocalizedString(
    localizedStrings.nodes,
    "localization_string___search",
    "Search"
  )
  const localizedOpenMenu = getLocalizedString(
    localizedStrings.nodes,
    "localization_string___open_menu",
    "Open Menu"
  )

  const [isSearchOpen, setIsSearchOpen] = useState(false)

  const closeSearch = () => setIsSearchOpen(false)
  const openSearch = () => setIsSearchOpen(true)

  const headerRef = useRef(null)

  // Determines current path without language prefix as fallback when otherLanguages doesn’t contain a link to the current page
  const { pathname } = useLocation()
  const defaultVariantPath =
    pathname && pathname.replace(/^\/(de|es|fr|it|pl|pt|jp|cn)/, "")

  let includeLocalNavWithNoLinks = false

  // Allow exceptions to include local nav in some areas of the sites where
  // child items aren’t included in Nav:
  // /products/arb-equipment – so there is an indication the SmartSingulator
  //   page is in the ARB section
  if (defaultVariantPath.indexOf("/products/arb-equipment") !== -1) {
    includeLocalNavWithNoLinks = true
  }

  useEffect(() => {
    if (!isClient) {
      return
    }

    const updateHeaderHeight = debounce(() => {
      if (!headerRef.current) {
        return
      }

      const headerElem = headerRef.current.getBoundingClientRect()

      setHeaderHeight(headerElem.bottom)
    }, 100)

    updateHeaderHeight()

    window.addEventListener("resize", updateHeaderHeight)

    var stickyElements = document.querySelectorAll(".js-sticky-header")
    Stickyfill.add(stickyElements)

    return () => {
      Stickyfill.remove(stickyElements)
      window.removeEventListener("resize", updateHeaderHeight)
    }
  }, [isClient, setHeaderHeight])

  return (
    <header
      className="bg-white sticky top-0 left-0 w-full z-40 js-sticky-header"
      ref={headerRef}
    >
      <div className="bg-gray-800 px-4 py-1 flex items-center relative z-20 leading-normal">
        <nav className="ml-auto lg:mr-0 text-xs sm:text-sm leading-normal sm:leading-normal">
          <ul className="flex items-center -mr-4 text-white">
            <li className="pr-2">
              <Link
                to={`${urlPrefix}/support/phone-numbers`}
                className={`hover:bg-black/25 focus:bg-black/25 inline-block rounded py-1 px-2 whitespace-nowrap focus:outline-none focus:ring`}
              >
                <i className="icon-phone icon-lg" aria-hidden="true"></i>{" "}
                {localizedCallUs}
              </Link>
            </li>
            <li className="pr-2 hidden sm:block">
              <Link
                to={`${urlPrefix}/request-a-quote`}
                className={`hover:bg-black/25 focus:bg-black/25 inline-block rounded py-1 px-2 whitespace-nowrap focus:outline-none focus:ring`}
              >
                {localizedGetAQuote}
              </Link>
            </li>
            <li className="pr-2">
              <Menu>
                <MenuButton
                  className={`hover:bg-black/25 focus:bg-black/25 inline-block rounded py-1 px-2 whitespace-nowrap focus:outline-none focus:ring`}
                >
                  <i className="icon-globe icon-lg"></i> {localizedLanguage}{" "}
                  <i className="icon-chevron-down"></i>
                </MenuButton>
                <MenuPopover
                  className={`z-40 text-xs sm:text-sm bg-white shadow-md border border-gray-300 rounded-b-lg mt-1 overflow-hidden`}
                >
                  <div>
                    <MenuItems className={`divide-y divide-gray-300`}>
                      {languages.map((language) => {
                        const languageLink = otherLanguages.find(
                          (otherLanguage) =>
                            otherLanguage.preferred_language ===
                            language.codename
                        )
                        const baseClassName = `block text-gray-900 px-4 py-2`
                        const activeClassName = `font-medium bg-gray-200`
                        const inactiveClassName = `hover:bg-gray-100 focus:bg-gray-100 font-normal`
                        const className = ({ isCurrent }) => {
                          return {
                            className: `${
                              isCurrent ? activeClassName : inactiveClassName
                            } ${baseClassName}`,
                          }
                        }
                        return (
                          <MenuLink
                            as={GatsbyLink}
                            getProps={className}
                            className={baseClassName}
                            key={language.codename}
                            to={
                              languageLink
                                ? urlHelper(languageLink)
                                : `${getUrlPrefix(
                                    language.codename
                                  )}${defaultVariantPath}` // Creates link to current URL with appropriate language prefix
                            }
                          >
                            {language.title}
                          </MenuLink>
                        )
                      })}
                    </MenuItems>
                  </div>
                </MenuPopover>
              </Menu>
            </li>
          </ul>
        </nav>
      </div>
      <div className="bg-white flex items-center justify-between relative z-10 shadow">
        <Link className="focus:outline-none focus:ring" to={`${urlPrefix}/`}>
          <img src={logo} alt="Intralox" className="header__logo" />
        </Link>
        <nav
          aria-label="Main navigation"
          className="absolute inset-0 justify-around items-center hidden lg:flex"
          style={{ marginLeft: `129px`, marginRight: `129px` }}
        >
          <MainNavigation
            currentLanguage={currentLanguage}
            global={global}
            localizedStrings={localizedStrings.nodes}
          />
        </nav>
        <div className="relative z-10 px-4">
          <div className="flex items-center -ml-6">
            <div className="pl-6">
              <button
                aria-controls="search"
                aria-label={localizedSearch}
                className="font-bold uppercase tracking-wide text-gray-900 focus:outline-none focus:ring"
                onClick={(event) => openSearch()}
              >
                <i className="icon-search text-gray-400 text-xl leading-none" />
                <span className={`hidden lg:inline lg:ml-1`}>
                  {localizedSearch}
                </span>
              </button>
            </div>
            <div className="pl-6 lg:hidden">
              <button
                className={`h-10 w-10 inline-block border border-gray-300 rounded`}
                aria-expanded="false"
                aria-controls="navbar"
                id="navbutton"
                onClick={pageCtx.toggleMenu}
              >
                <span className="sr-only">{localizedOpenMenu}</span>
                <i className="icon-menu icon-lg"></i>
              </button>
            </div>
          </div>
        </div>
        {isSearchOpen && (
          <Search
            currentLanguage={currentLanguage}
            closeSearch={closeSearch}
            localizedStrings={localizedStrings}
          />
        )}
      </div>
      {(localNav && localNav.length > 1) || includeLocalNavWithNoLinks ? (
        <LocalNav entry={entry} links={localNav} />
      ) : null}
    </header>
  )
}

export interface SearchProps {
  /**
   * Current language of the website
   * Used to set localized paths for search form
   */
  currentLanguage: HeaderProps["currentLanguage"]
  /**
   * Sets the value of if the search is focused and open, or closed and hidden
   */
  closeSearch: () => void
  /**
   * An array of language-specific localized strings
   */
  localizedStrings: any
}

const Search = ({
  currentLanguage,
  closeSearch,
  localizedStrings,
}: SearchProps) => {
  const [query, setQuery] = useState("")
  const urlPrefix = getUrlPrefix(currentLanguage)
  const localizedCancel = getLocalizedString(
    localizedStrings.nodes,
    "localization_string___cancel",
    "Cancel"
  )
  const localizedSearch = getLocalizedString(
    localizedStrings.nodes,
    "localization_string___search",
    "Search"
  )
  const localizedSearchIntraloxCom = getLocalizedString(
    localizedStrings.nodes,
    "localization_string___search_intralox_com"
  )

  const location = useLocation()
  const getLastItem = location.pathname.substring(
    location.pathname.lastIndexOf("/") + 1
  )

  const handleSubmit = (evt) => {
    evt.preventDefault()

    //8.24.21 - kc - workaround for header search box not updating search results when on search results page
    if (getLastItem === "search") {
      window.location = `${urlPrefix}/search?query=${query}`
    } else {
      navigate(`${urlPrefix}/search?query=${query}`, { state: { query } })
    }
  }

  const handleReset = () => {
    closeSearch()
  }

  const handleKeyDown = useCallback(
    (event) => {
      switch (event.key) {
        case "Escape": // esc
          // Pressing esc should close the dropdown and restore focus to the top level item
          closeSearch()
          break
        default:
          break
      }
    },
    [closeSearch]
  )

  useEffect(() => {
    document.addEventListener("keydown", handleKeyDown)

    return () => {
      document.removeEventListener("keydown", handleKeyDown)
    }
  }, [handleKeyDown])

  return (
    <div
      id="search"
      className={` inset-0 absolute h-full flex items-center justify-center bg-white z-10 ml-logo--sm md:ml-logo--lg lg:ml-logo--lg lg:pr-logo--lg pr-4`}
    >
      <form
        action={`${urlPrefix}/search`}
        className={`relative w-full flex items-center max-w-2xl`}
        method="get"
        onSubmit={handleSubmit}
      >
        <input
          autoFocus // eslint-disable-line
          className={`bg-gray-100 border border-gray-300 p-2 w-full pr-10 rounded appearance-none search-decoration-none focus:outline-none focus:ring`}
          placeholder={localizedSearchIntraloxCom}
          type="text"
          onChange={(e) => setQuery(e.target.value)}
        />
        <div
          className={`absolute right-0 top-1/2 -legacy-translate-y-1/2 mr-2 flex`}
        >
          <div className={`border-r border-gray-300 pr-4`}>
            <button
              className={`text-gray-600 flex items-center justify-center h-6 w-6 rounded-full focus:outline-none focus:ring`}
              aria-label={localizedSearch}
              type="submit"
            >
              <i
                className={`icon-search icon-lg text-gray-400`}
                aria-hidden="true"
              ></i>
            </button>
          </div>
          <div className={`pl-4`}>
            <button
              className={`text-gray-600 flex items-center justify-center h-6 w-6 rounded-full focus:outline-none focus:ring`}
              aria-label={localizedCancel}
              type="reset"
              onClick={() => handleReset()}
            >
              <i
                className={`icon-x icon-lg text-gray-400`}
                aria-hidden="true"
              ></i>
            </button>
          </div>
        </div>
      </form>
    </div>
  )
}

export default Header
